import { ImagePlus, X } from 'lucide-react';
import { Avatar } from 'radix-ui';
import { type ChangeEvent, useEffect, useRef } from 'react';
import DefaultUser from '@/assets/store/icon/user-photo.svg';
import type { DonationUser } from '@/domains/mobile-web/components/CardDonationPost/CardDonationPost';
import { cn } from '@/lib/utils';

interface ComposerActionBarProps {
  submitLabel?: string;
  cancelLabel?: string;
  disabled?: boolean;
  onCancel?: () => void;
  onSubmit?: () => void;
  onGallery?: () => void;
  className?: string;
}

export const ComposerActionBar = ({
  submitLabel = 'Post',
  cancelLabel = 'Cancel',
  disabled = false,
  onCancel,
  onSubmit,
  onGallery,
  className,
}: ComposerActionBarProps) => (
  <div
    className={cn(
      'w-full inline-flex items-center justify-between py-2 border-b border-blue-cs-200',
      className
    )}
  >
    <button type="button" onClick={onCancel} className="body1 text-text-grey">
      {cancelLabel}
    </button>

    <div className="items-center flex w-fit gap-3">
      {onGallery && (
        <button
          type="button"
          onClick={onGallery}
          disabled={disabled}
          className=" text-text-blue-100"
        >
          <ImagePlus />
        </button>
      )}
      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled}
        className="title3bold rounded-full bg-text-blue-100 px-6 py-2 text-white transition-opacity disabled:opacity-50"
      >
        {submitLabel}
      </button>
    </div>
  </div>
);

interface PostComposerProps {
  user: DonationUser;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  replyTo?: DonationUser;
  maxLength?: number;
  autoFocus?: boolean;
  className?: string;
  media?: string[];
  onRemoveMedia?: (index: number) => void;
  showAvatar?: boolean;
}

const PostComposer = ({
  user,
  value,
  onChange,
  placeholder = 'Tulis sesuatu..',
  replyTo,
  maxLength,
  autoFocus = true,
  className,
  media = [],
  showAvatar = false,
  onRemoveMedia,
}: PostComposerProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure the textarea whenever the text changes
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value);

  return (
    <div className={cn('flex gap-3 w-full', className)}>
      {showAvatar && (
        <figure className="w-[48px] h-[48px] shrink-0 rounded-full overflow-hidden bg-bg-grey-100">
          <img
            src={user.avatar || DefaultUser}
            alt={user.name}
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
          />
        </figure>
      )}

      <div className="min-w-0 flex-1 flex flex-col gap-1 pt-1">
        {replyTo && (
          <p className="caption text-text-grey">
            Balas pesan dari <span className="text-text-blue-100">{replyTo.name}</span>
          </p>
        )}
        {media.length > 0 && (
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pt-2 -mr-3 pr-3">
            {media.map((src, index) => (
              <figure
                key={`${src}-${index}`}
                className="relative shrink-0 w-[96px] h-[96px] rounded-[8px] overflow-hidden bg-bg-grey-100"
              >
                <img src={src} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
                {onRemoveMedia && (
                  <button
                    type="button"
                    onClick={() => onRemoveMedia(index)}
                    aria-label={`Hapus foto ${index + 1}`}
                    className="absolute top-1 right-1 rounded-full bg-black/60 text-white p-0.5"
                  >
                    <X size={14} />
                  </button>
                )}
              </figure>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          maxLength={maxLength}
          /* biome-ignore lint/a11y/noAutofocus: the composer is the only input on the page, so the keyboard should open at once */
          autoFocus={autoFocus}
          rows={1}
          className="w-full resize-none bg-transparent border-0 p-0 outline-none body1 text-black placeholder:text-text-grey"
        />
      </div>
    </div>
  );
};

export default PostComposer;
